/**
 * LE VOILE HORS LIGNE — le monde s'arrete quand on n'y est plus.
 *
 * Le jeu continuait tout seul apres une coupure: un message de quatre secondes,
 * puis plus rien. On marchait, on batissait, on cueillait dans un monde que
 * personne d'autre ne voyait, et au retour de la liaison le serveur reprenait la
 * main sur les positions. Surtout, RIEN A L'ECRAN NE DISTINGUAIT « je suis
 * seul » de « je suis coupe »: c'est exactement ce qui a fait croire pendant
 * tout un apres-midi qu'on ne voyait jamais les autres joueurs, alors que la
 * liaison tombait toutes les dix minutes.
 *
 * Le voile prend TOUS les appuis. C'est voulu: tant qu'il est la, le monde ne
 * bouge pas, donc laisser passer un tap donnerait un geste sans effet, ce qui
 * est pire qu'un geste refuse.
 *
 * Il ne concerne QUE le monde partage. Un invite ne se connecte jamais au
 * salon: pour lui il n'y a rien a perdre, et le voile ne s'affiche jamais.
 */
import { journal } from '../log/journal.ts'
import { el } from './commun.ts'
import '../styles/hors-ligne.css'

export class VoileHorsLigne {
  private readonly racine: HTMLDivElement
  private readonly titre: HTMLParagraphElement
  private readonly detail: HTMLParagraphElement
  private visible = false
  /** Depuis quand la coupure dure, pour l'ecrire au joueur. */
  private depuis = 0
  private dernierAffiche = -1

  constructor(parent: HTMLElement) {
    this.racine = el('div', 'hors-ligne')
    this.racine.hidden = true
    // Une region vivante: le lecteur d'ecran annonce la coupure sans qu'on ait
    // a lui voler le focus.
    this.racine.setAttribute('role', 'status')
    this.racine.setAttribute('aria-live', 'polite')

    const boite = el('div', 'hors-ligne__boite')
    boite.append(el('div', 'hors-ligne__rondelle'))
    this.titre = el('p', 'hors-ligne__titre', 'Liaison perdue')
    this.detail = el('p', 'hors-ligne__detail', 'Le monde est en pause. Reconnexion…')
    boite.append(this.titre, this.detail)
    this.racine.append(boite)
    parent.append(this.racine)
  }

  get affiche(): boolean {
    return this.visible
  }

  /**
   * Montre le voile. `raison` vient du serveur quand il REFUSE la liaison —
   * version du jeu trop ancienne, jeton inconnu: c'est une phrase deja ecrite
   * en francais, qu'il serait absurde de remplacer par « reconnexion » alors
   * qu'aucune reconnexion n'y changera rien.
   */
  montrer(raison: string | null): void {
    if (raison !== null) {
      this.titre.textContent = 'Impossible de rejoindre le monde'
      this.detail.textContent = raison
      this.racine.classList.add('hors-ligne--refus')
    } else {
      this.titre.textContent = 'Liaison perdue'
      this.racine.classList.remove('hors-ligne--refus')
    }
    if (this.visible) return
    this.visible = true
    this.depuis = 0
    this.dernierAffiche = -1
    this.racine.hidden = false
    // Deux images pour que la transition parte de l'etat cache.
    requestAnimationFrame(() => this.racine.classList.add('hors-ligne--ouvert'))
    journal.warn('reseau', raison !== null ? `liaison refusee: ${raison}` : 'monde en pause: liaison perdue')
  }

  /** Fait vieillir le compteur. A appeler meme quand le monde est en pause. */
  maj(dt: number, raison: string | null): void {
    if (!this.visible || raison !== null) return
    this.depuis += dt
    const s = Math.floor(this.depuis)
    if (s === this.dernierAffiche) return
    this.dernierAffiche = s
    this.detail.textContent =
      s < 3 ? 'Le monde est en pause. Reconnexion…' : `Le monde est en pause. Reconnexion… (${s} s)`
  }

  cacher(): void {
    if (!this.visible) return
    this.visible = false
    this.racine.classList.remove('hors-ligne--ouvert')
    this.racine.hidden = true
    journal.info('reseau', 'monde repris')
  }

  detruire(): void {
    this.racine.remove()
  }
}
